import type { HTMLAttributes } from 'react'
import React, { forwardRef, useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { css, cx } from '@linaria/core'
import { ObserverRootContext, useForwardRef, useResizeObserver } from '@fs/zion-frontend-friends'
import { colors } from '../index'

const containerCss = css`
  display: flex;
  flex-direction: column;
  flex: 1;
  -ms-overflow-style: -ms-autohiding-scrollbar; /* Makes the scrollbar auto-hide/show on edge */

  /* better mobile scrolling */
  overflow-scrolling: touch;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;

  overflow-x: hidden;
  min-height: 0;

  /* Add custom focus ring for keyboard navigation */
  &:focus-visible {
    outline: 3px solid ${colors.blue60};
    outline-offset: -3px; /* Adjusts the outline to be snug against the content */
    border-radius: 4px;
  }
`

const contentCss = css`
  display: flex;
  flex-direction: column;
  flex: 1;
`

type Props = HTMLAttributes<HTMLDivElement> & {
  /** Whether to hide the borders temporarily */
  hideBorders?: boolean

  /** Prevents the scrollbar from appearing and disappearing (useful for animations) */
  freezeScrollbar?: boolean
}

export const BorderScroller = forwardRef<HTMLDivElement, Props>(
  (
    { children, className, hideBorders, freezeScrollbar, style, ...props }: Props,
    ref: React.Ref<HTMLDivElement>
  ): JSX.Element => {
    const [scrolledToTop, setScrolledToTop] = useState(true)
    const [scrolledToBottom, setScrolledToBottom] = useState(false)
    const [overflowY, setOverflowY] = useState<'auto' | 'hidden' | 'scroll'>('auto')
    const scrollerRef = useForwardRef<HTMLDivElement | null>(ref)
    const contentRef = useForwardRef<HTMLDivElement | null>(ref)

    const handleScroll = useCallback(() => {
      const scrollerEl = scrollerRef.current
      if (!scrollerEl) return
      const scrollableAmount = scrollerEl.scrollHeight - scrollerEl.offsetHeight
      setScrolledToTop(scrollerEl.scrollTop === 0)
      // use "- 1" here for a little bit of wiggle room
      setScrolledToBottom(scrollerEl.scrollTop >= scrollableAmount - 1)
    }, [scrollerRef])

    useResizeObserver(contentRef, handleScroll)

    useEffect(() => {
      if (!freezeScrollbar) {
        setOverflowY('auto')
        return
      }

      const scrollerEl = scrollerRef.current
      // @ts-expect-error -- TODO fix strict issue: This was grandfathered from before we adopted strict mode in typescript.
      const hasScrollbar = scrollerEl.offsetWidth - scrollerEl.clientWidth > 0
      setOverflowY(hasScrollbar ? 'scroll' : 'hidden')
    }, [freezeScrollbar, scrollerRef])

    return (
      <div
        ref={scrollerRef}
        {...props}
        style={{
          ...style,
          overflowY,
          borderTop: scrolledToTop || hideBorders ? undefined : `1px solid ${colors.transparent.gray10}`,
          borderBottom: scrolledToBottom || hideBorders ? undefined : `1px solid ${colors.transparent.gray10}`,
        }}
        className={cx(className, containerCss)}
        onScroll={handleScroll}
        data-scrollable=""
        {...(scrolledToBottom && scrolledToTop ? undefined : { tabIndex: 0 })}
      >
        <div className={contentCss} ref={contentRef}>
          <ObserverRootContext.Provider value={scrollerRef}>{children}</ObserverRootContext.Provider>
        </div>
        {/* We need to put the border on as an absolute element because we want the border to be on the inside edge of the content, not wrapping it, so it can use its transparent powers. This is also better than an inset box-shadow because bleeding content will cover up the box-shadow */}
      </div>
    )
  }
)

export default BorderScroller

BorderScroller.propTypes = {
  /** The content of the scrollable container. */
  children: PropTypes.node,
}
